'use client';

import { cn } from '@/app/utils';
import { type TestimonialAuthor, TestimonialCard } from './testimonial-card';

interface Testimonial {
  author: TestimonialAuthor;
  text: string;
  href?: string;
}

interface TestimonialsSectionProps {
  title: string;
  description: string;
  testimonials: Testimonial[];
  className?: string;
}

/**
 * 单列纵向无缝滚动：2× 内容 + translateY ∓50% 回到起点帧。gap-6 上下一致，-50% 恰好错开一个周期。
 * hover 暂停。方向由 reverse 决定（相邻列反向）。
 */
function MarqueeColumn({ items, reverse }: { items: Testimonial[]; reverse?: boolean }) {
  const track = [...items, ...items];
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div
        className={cn(
          'group/col flex shrink-0 flex-col gap-6 pb-6',
          reverse ? 'animate-scroll-down' : 'animate-scroll-up',
          'motion-reduce:animate-none hover:[animation-play-state:paused]',
        )}
      >
        {track.map((t, i) => (
          <TestimonialCard key={`${t.author.name}-${i}`} {...t} />
        ))}
      </div>
    </div>
  );
}

/**
 * 证言墙（用户声音）：三列纵向滚动，相邻列方向相反，上下渐隐遮罩。
 * 每列内均为普通文字卡（头像在姓名左侧）。
 *
 * 标题 / 副标题与第四屏 bento 同款（字号、字重、行高、间距）。
 * 断点反向（本仓库 md/sm 是 max-width）：桌面 3 列，md（≤1023）2 列，sm（≤767）1 列。
 */
export function TestimonialsSection({ title, description, testimonials, className }: TestimonialsSectionProps) {
  // 轮流分到 3 列，保证每列条数均衡
  const columns: Testimonial[][] = [[], [], []];
  testimonials.forEach((t, i) => {
    columns[i % 3].push(t);
  });

  return (
    <section className={cn('bg-black py-32 text-white sm:py-12 md:py-24', className)}>
      {/* 容器响应式与第四屏 bento 同规则（mx-auto w-full px-10 md:px-4），最大宽度 1280 */}
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-10 md:px-4">
        <h2 className="mx-auto max-w-[46rem] text-center font-semibold text-[3.5rem] text-white leading-[1.05] tracking-tight [text-wrap:balance] md:text-[2.5rem] lg:text-[3rem]">
          {title}
        </h2>
        <p className="mt-4 text-center font-normal text-[16px] text-system-white64 md:mt-3">{description}</p>

        {/* 三列滚动容器：固定高度 + 上下渐隐遮罩 */}
        <div className="relative mt-16 h-[640px] w-full overflow-hidden sm:h-[520px] md:mt-10 md:h-[560px]">
          <div className="flex h-full gap-6 sm:gap-4">
            {/* 桌面 3 列；md（≤1023）2 列，隐第 3 列；sm（≤767）1 列，隐第 2、3 列 */}
            <MarqueeColumn items={columns[0]} />
            <div className="flex flex-1 sm:hidden">
              <MarqueeColumn items={columns[1]} reverse />
            </div>
            <div className="flex flex-1 md:hidden">
              <MarqueeColumn items={columns[2]} />
            </div>
          </div>

          {/* 上下渐隐遮罩 */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent" />
        </div>
      </div>
    </section>
  );
}
